import { useCallback, useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import ProtectedRoute from "../../components/ProtectedRoute";
import { AuthProvider } from "../../context/AuthContext";
import Header from "../../components/admin/Header";
import Sidebar from "../../components/admin/Sidebar";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isVerified: boolean;
  lastLoginAt: string;
  profileImageUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface EditUserForm {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

function useTheme() {
  const [theme, setThemeState] = useState<"light" | "dark">("light");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const storedTheme =
      (localStorage.getItem("theme") as "light" | "dark") || "light";
    setThemeState(storedTheme);
    setMounted(true);
  }, []);

  const setTheme = useCallback((newTheme: "light" | "dark") => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);
  }, []);

  useEffect(() => {
    if (mounted) {
      document.documentElement.classList.toggle("dark", theme === "dark");
    }
  }, [theme, mounted]);

  return { theme, setTheme, mounted };
}

function ManageUsersContent() {
  const { theme, setTheme } = useTheme();
  const { logout, token: authToken } = useAuth();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editForm, setEditForm] = useState<EditUserForm>({
    firstName: "",
    lastName: "",
    email: "",
    role: "",
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "verified" | "unverified">("all");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserId, setNewUserId] = useState("");
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const API_BASE_URL =
    import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!authToken) throw new Error("No authentication token available");
      const response = await fetch(`${API_BASE_URL}/users`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error("Failed to fetch users");
      const data = await response.json();
      setUsers(data.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, authToken]);

  const createUser = useCallback(
    async (formData: EditUserForm) => {
      try {
        if (!authToken) throw new Error("No authentication token available");
        if (!formData.role) throw new Error("Role is required");
        const response = await fetch(`${API_BASE_URL}/users`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ...formData }),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create user");
        }
        const data = await response.json();
        const createdUser = data.data || data;
        await fetchUsers();
        setShowEditModal(false);
        setIsCreating(false);
        setNewUserEmail(formData.email);
        setNewUserId(createdUser.id);
        setShowEmailModal(true);
        setEditForm({ firstName: "", lastName: "", email: "", role: "" });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to create user");
      }
    },
    [API_BASE_URL, authToken, fetchUsers]
  );

  const resendVerificationEmail = useCallback(async () => {
    if (!newUserId) return;
    try {
      setResendingEmail(true);
      setResendSuccess(false);
      if (!authToken) throw new Error("No authentication token available");
      const response = await fetch(
        `${API_BASE_URL}/auth/resend-verification/${newUserId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to resend verification email");
      }
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resend verification email");
      setTimeout(() => setError(null), 3000);
    } finally {
      setResendingEmail(false);
    }
  }, [API_BASE_URL, authToken, newUserId]);

  const updateUser = useCallback(
    async (userId: string, formData: EditUserForm) => {
      try {
        if (!authToken) throw new Error("No authentication token available");
        const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        });
        if (!response.ok) throw new Error("Failed to update user");
        await fetchUsers();
        setShowEditModal(false);
        setEditingUser(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to update user");
      }
    },
    [API_BASE_URL, authToken, fetchUsers]
  );

  const deleteUser = useCallback(
    async (userId: string) => {
      try {
        if (!authToken) throw new Error("No authentication token available");
        const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) throw new Error("Failed to delete user");
        await fetchUsers();
        setDeletingUserId(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to delete user");
      }
    },
    [API_BASE_URL, authToken, fetchUsers]
  );

  const toggleVerified = useCallback(
    async (userId: string, currentVerified: boolean) => {
      try {
        if (!authToken) throw new Error("No authentication token available");
        const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isVerified: !currentVerified }),
        });
        if (!response.ok) throw new Error("Failed to update verification status");
        await fetchUsers();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to update verification status");
      }
    },
    [API_BASE_URL, authToken, fetchUsers]
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = users.filter(
    (user) =>
      filter === "all" ||
      (filter === "verified" ? user.isVerified : !user.isVerified)
  );

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = () => {
    if (editingUser) {
      updateUser(editingUser.id, editForm);
    } else if (isCreating) {
      if (!editForm.role) {
        setError("Role is required");
        return;
      }
      createUser(editForm);
    }
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setEditingUser(null);
    setEditForm({ firstName: "", lastName: "", email: "", role: "DATA_COLLECTOR" });
    setShowEditModal(true);
  };

  const handleDelete = (userId: string) => {
    if (confirm("Are you sure you want to delete this user?")) {
      deleteUser(userId);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setIsCreating(false);
    setEditingUser(null);
    setEditForm({ firstName: "", lastName: "", email: "", role: "" });
  };

  const roleColor = (role: string) => {
    switch (role) {
      case "ADMIN": return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400";
      case "SUPERVISOR": return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
      case "DATA_COLLECTOR": return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case "INSTITUTIONAL_CLIENT": return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    }
  };

  const roleLabel = (role: string) => {
    switch (role) {
      case "DATA_COLLECTOR": return "Collector";
      case "INSTITUTIONAL_CLIENT": return "Client";
      default: return role.charAt(0) + role.slice(1).toLowerCase();
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 dark:border-gray-400"></div>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
        <Header theme={theme} setTheme={setTheme} logout={logout} />
        <Sidebar onGenerateReport={() => {}} />
        <div className="ml-0 md:ml-64 p-6 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
          <p className="text-red-700 mt-10 dark:text-red-300">{error}</p>
          <button
            onClick={fetchUsers}
            className="mt-3 bg-red-200 dark:bg-red-800 px-3 py-1 rounded text-red-700 dark:text-red-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
      <Header theme={theme} setTheme={setTheme} logout={logout} />

      <div className="flex">
        <Sidebar onGenerateReport={() => {}} />

        <main className="flex-1 lg:ml-64 overflow-x-hidden">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-8">

            {/* Page title */}
            <h1 className="text-xl sm:text-2xl font-semibold mb-5 text-gray-900 dark:text-white">
              Manage Users
            </h1>

            {/* Top bar */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 sm:justify-between mb-5">
              {/* Create button */}
              <button
                onClick={handleCreateNew}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-700 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors w-full sm:w-auto"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create New User
              </button>

              {/* Filter pills */}
              <div className="flex bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden w-full sm:w-auto">
                {(["all", "verified", "unverified"] as const).map((value, i, arr) => (
                  <button
                    key={value}
                    onClick={() => setFilter(value)}
                    className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors capitalize
                      ${i === 0 ? "rounded-l-md" : ""}
                      ${i === arr.length - 1 ? "rounded-r-md" : ""}
                      ${filter === value
                        ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {[
                { label: "Total Users", value: users.length, color: "text-gray-700 dark:text-gray-200" },
                { label: "Verified", value: users.filter(u => u.isVerified).length, color: "text-green-600 dark:text-green-400" },
                { label: "Unverified", value: users.filter(u => !u.isVerified).length, color: "text-amber-600 dark:text-amber-400" },
                { label: "Showing", value: filteredUsers.length, color: "text-blue-600 dark:text-blue-400" },
              ].map(stat => (
                <div key={stat.label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</p>
                  <p className={`text-2xl font-bold mt-0.5 ${stat.color}`}>{stat.value}</p>
                </div>
              ))}
            </div>

            {/* ── MOBILE / TABLET: Card Grid ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:hidden">
              {filteredUsers.map((user) => {
                const fullName = `${user.firstName} ${user.lastName}`;
                const initials = `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
                const lastLogin = user.lastLoginAt
                  ? new Date(user.lastLoginAt).toLocaleString()
                  : "Never";

                return (
                  <div
                    key={user.id}
                    className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 flex flex-col gap-3"
                  >
                    {/* Top: avatar + name + role */}
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        {user.profileImageUrl ? (
                          <img
                            src={user.profileImageUrl}
                            className="h-10 w-10 rounded-full object-cover"
                            alt=""
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-sm font-semibold text-gray-600 dark:text-gray-300">
                            {initials}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{fullName}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                      </div>
                      <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full ${roleColor(user.role)}`}>
                        {roleLabel(user.role)}
                      </span>
                    </div>

                    {/* Middle: last login + verified */}
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-2">
                      <span>Last login: <span className="text-gray-700 dark:text-gray-300">{lastLogin}</span></span>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={user.isVerified}
                          onChange={() => toggleVerified(user.id, user.isVerified)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
                        />
                        <span className={user.isVerified ? "text-green-600 dark:text-green-400 font-medium" : "text-gray-400"}>
                          {user.isVerified ? "Verified" : "Unverified"}
                        </span>
                      </label>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(user)}
                        className="flex-1 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="flex-1 py-1.5 text-xs font-medium rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}

              {filteredUsers.length === 0 && (
                <div className="col-span-2 text-center py-12 text-gray-400 dark:text-gray-500 text-sm">
                  No users found.
                </div>
              )}
            </div>

            {/* ── DESKTOP: Scrollable Table ── */}
            <div className="hidden lg:block bg-white dark:bg-gray-800 shadow rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      {["Name", "Email", "Role", "Verified", "Last Login", "Actions"].map((col) => (
                        <th
                          key={col}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredUsers.map((user) => {
                      const fullName = `${user.firstName} ${user.lastName}`;
                      const initials = `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
                      const lastLogin = user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleString()
                        : "Never";

                      return (
                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-9 w-9 flex-shrink-0">
                                {user.profileImageUrl ? (
                                  <img src={user.profileImageUrl} className="h-9 w-9 rounded-full object-cover" alt="" />
                                ) : (
                                  <div className="h-9 w-9 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-semibold text-gray-600 dark:text-gray-300">
                                    {initials}
                                  </div>
                                )}
                              </div>
                              <span className="ml-3 text-sm font-medium text-gray-900 dark:text-white">{fullName}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{user.email}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${roleColor(user.role)}`}>
                              {roleLabel(user.role)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={user.isVerified}
                              onChange={() => toggleVerified(user.id, user.isVerified)}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
                            />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">{lastLogin}</td>
                          <td className="px-6 py-4 text-sm font-medium">
                            <div className="flex space-x-3">
                              <button
                                onClick={() => handleEdit(user)}
                                className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(user.id)}
                                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredUsers.length === 0 && (
                  <div className="text-center py-12 text-gray-400 dark:text-gray-500 text-sm">
                    No users found.
                  </div>
                )}
              </div>
            </div>

            {/* ── Edit / Create Modal ── */}
            {showEditModal && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
                <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md overflow-hidden">
                  {/* Header */}
                  <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex items-start justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        {isCreating ? "Create New User" : "Edit User"}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {isCreating
                          ? "Fill in the details to add a new user to the system."
                          : "Update the user's information below."}
                      </p>
                    </div>
                    <button
                      onClick={closeEditModal}
                      className="ml-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Body */}
                  <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                          First Name
                        </label>
                        <input
                          type="text"
                          value={editForm.firstName}
                          onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition"
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={editForm.lastName}
                          onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition"
                          placeholder="Doe"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                          className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Role
                      </label>
                      <div className="relative">
                        <select
                          value={editForm.role}
                          onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent appearance-none transition"
                        >
                          <option value="">Select a role</option>
                          <option value="ADMIN">Admin</option>
                          <option value="SUPERVISOR">Supervisor</option>
                          <option value="DATA_COLLECTOR">Data Collector</option>
                          <option value="INSTITUTIONAL_CLIENT">Institutional Client</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/60 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-3">
                    <button
                      onClick={closeEditModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      className="px-5 py-2 text-sm font-medium text-white bg-gray-800 dark:bg-gray-600 hover:bg-gray-900 dark:hover:bg-gray-500 rounded-lg transition-colors shadow-sm"
                    >
                      {isCreating ? "Create User" : "Save Changes"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Check Your Email Modal ── */}
            {showEmailModal && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
                <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md overflow-hidden">
                  <div className="px-6 pt-6 pb-4 text-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/40 mb-4">
                      <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      Verification Email Sent
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5 leading-relaxed">
                      A verification link has been sent to{" "}
                      <span className="font-semibold text-gray-700 dark:text-gray-200">{newUserEmail}</span>.
                      The user should check their inbox and follow the instructions to activate their account.
                    </p>
                  </div>

                  <div className="border-t border-gray-100 dark:border-gray-800 mx-6" />

                  <div className="px-6 py-4 text-center">
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">Didn't receive it?</p>
                    <button
                      onClick={resendVerificationEmail}
                      disabled={resendingEmail}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors disabled:opacity-60"
                    >
                      {resendingEmail ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Sending…
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Resend verification email
                        </>
                      )}
                    </button>
                    {resendSuccess && (
                      <p className="mt-2 text-xs text-green-600 dark:text-green-400 font-medium">
                        ✓ Verification email resent successfully
                      </p>
                    )}
                  </div>

                  <div className="px-6 pb-6">
                    <button
                      onClick={() => {
                        setShowEmailModal(false);
                        setNewUserEmail("");
                        setNewUserId("");
                        setResendSuccess(false);
                      }}
                      className="w-full px-4 py-2.5 text-sm font-medium text-white bg-gray-800 dark:bg-gray-600 hover:bg-gray-900 dark:hover:bg-gray-500 rounded-lg transition-colors shadow-sm"
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Delete Confirmation Modal ── */}
            {deletingUserId && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
                <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm overflow-hidden">
                  <div className="px-6 pt-6 pb-4 text-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/40 mb-4">
                      <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">Delete User</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5">
                      This action is permanent and cannot be undone. Are you sure you want to proceed?
                    </p>
                  </div>
                  <div className="px-6 pb-6 flex gap-3">
                    <button
                      onClick={() => setDeletingUserId(null)}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => deleteUser(deletingUserId)}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}

export default function ManageUsers() {
  return (
    <AuthProvider>
      <ProtectedRoute allowedRoles={["ADMIN"]}>
        <ManageUsersContent />
      </ProtectedRoute>
    </AuthProvider>
  );
}